import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import {
  AccountSelectors,
  CurrentUserEntityActions,
  UserActions,
  UserSelectors,
} from '@core/account-data';
import { AsyncJobService, AsyncJobStatus } from '@core/async-job-data';
import { AsyncJobType } from '@core/async-job-types';
import { filterBoolean } from '@core/common-legacy';
import type { Company } from '@core/company-types';
import { I18nService } from '@core/i18n-legacy';
import { AppStore } from '@core/legacy-app-store-legacy';
import { JobsWrapperActionsService } from '@core/offers-data';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';
import type { SampleDataRemoveContext } from '@recruitee/company-logic';
import { SampleDataAnalyticsService } from '@recruitee/company-logic';
import { SampleDataActions } from '@recruitee/sample-data-data';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { NoAccessDialogComponent } from '@recruitee/sample-data-smart-ui';
import { ActionType, fetchRecords as searchFetchRecords } from '@recruitee/search-data';

@Injectable({ providedIn: 'root' })
export class SampleDataService {
  private readonly jobsWrapperActionsService = inject(JobsWrapperActionsService);
  private readonly dialogService = inject(DialogService);
  private readonly i18nService = inject(I18nService);
  private readonly asyncJobsService = inject(AsyncJobService);
  private readonly appStore = inject(AppStore);
  private readonly store = inject(Store);
  private readonly sampleDataAnalyticsService = inject(SampleDataAnalyticsService);
  private readonly actions$ = inject(Actions);
  private readonly currentCompany$ = this.store
    .select(AccountSelectors.currentCompany)
    .pipe(filterBoolean);
  private readonly companyAdmins$ = this.currentCompany$.pipe(
    tap(() => this.store.dispatch(UserActions.fetchCollection())),
    switchMap(currentCompany => this.store.select(UserSelectors.entities())),
    filterBoolean,
    map(admins => admins.filter(admin => admin.roleAdmin)),
  );
  private readonly companyLoaded$ = this.store.select(AccountSelectors.currentCompany).pipe(
    filterBoolean,
    map(company => !!company),
    take(1),
  );
  public readonly hasSampleData$: Observable<boolean> = this.store
    .select(AccountSelectors.currentCompany)
    .pipe(map(({ hasSampleData }: Company) => hasSampleData));

  public readonly sampleDataInProgress$: Observable<boolean> = combineLatest([
    this.asyncJobsService.getJobs$(AsyncJobType.RemoveSampleData),
    this.asyncJobsService.getJobs$(AsyncJobType.GenerateSampleData),
  ]).pipe(
    map(([removeSampleDataJobs, generateSampleDataJobs]) =>
      [...removeSampleDataJobs, ...generateSampleDataJobs].some(
        job => job.status !== AsyncJobStatus.FINISHED,
      ),
    ),
    switchMap(inProgress =>
      inProgress
        ? of(inProgress)
        : of(inProgress).pipe(
            switchMap(value =>
              combineLatest([
                this.actions$.pipe(ofType(ActionType.FETCH_RECORDS_SUCCESS)).pipe(take(1)),
                this.actions$
                  .pipe(ofType(this.jobsWrapperActionsService.fetchActiveCollectionSuccessActions))
                  .pipe(take(1)),
                this.actions$.pipe(ofType(CurrentUserEntityActions.fetchSuccess)).pipe(take(1)),
              ]).pipe(map(() => value)),
            ),
          ),
    ),
    startWith(false),
  );

  public readonly sampleDataFinished$ = this.sampleDataInProgress$.pipe(
    filterBoolean,
    switchMap(() =>
      combineLatest([
        this.asyncJobsService.getJobs$(AsyncJobType.RemoveSampleData),
        this.asyncJobsService.getJobs$(AsyncJobType.GenerateSampleData),
      ]).pipe(
        map(jobs => {
          const sampleDataJobs = jobs.flat();
          const jobsNotFinished = sampleDataJobs.some(
            job => job.status !== AsyncJobStatus.FINISHED,
          );
          return !jobsNotFinished;
        }),
        distinctUntilChanged(),
        filterBoolean,
        debounceTime(0),
      ),
    ),
  );

  public readonly appDataRefresh$ = combineLatest([this.sampleDataFinished$, this.companyLoaded$]);
  private currentCompany: Company;

  constructor() {
    this.appDataRefresh$.subscribe(() => {
      this.store.dispatch(searchFetchRecords());
      this.jobsWrapperActionsService.fetchActiveCollection();
      this.store.dispatch(CurrentUserEntityActions.fetch());
    });

    this.currentCompany$.subscribe(currentCompany => (this.currentCompany = currentCompany));
  }

  public onRemoveSampleData(origin: SampleDataRemoveContext): void {
    const canRemoveData = this.currentCompany?.isAdmin;
    if (canRemoveData) {
      this.dialogService
        .information({
          title: this.i18nService.translate('app.onboarding.trial_center.confirm_remove.title'),
          message: this.i18nService.translate('app.onboarding.trial_center.confirm_remove.message'),
          agreeLabel: this.i18nService.translate(
            'app.onboarding.trial_center.confirm_remove.agree_label',
          ),
          width: DialogWidth.SmallXX,
        })
        .pipe(take(1), filterBoolean)
        .subscribe(_ => {
          this.sampleDataAnalyticsService.trackRemoveSampleData(origin);
          this.store.dispatch(SampleDataActions.deleteSampleData());
        });
    } else {
      this.companyAdmins$.pipe(take(1)).subscribe(admins => {
        this.dialogService.openCustom(
          NoAccessDialogComponent,
          { width: DialogWidth.Small },
          {
            title: this.i18nService.translate('app.onboarding.no_access_dialog.remove_data.title'),
            description: this.i18nService.translate(
              'app.onboarding.no_access_dialog.remove_data.description',
            ),
            imageSrc: 'assets/images/onboarding/removing-sample-data.svg',
            admins: admins.slice(0, 3),
          },
        );
      });
    }
  }

  public onAddSampleData(): void {
    const canAddData = this.currentCompany?.isAdmin;
    if (canAddData) {
      this.sampleDataAnalyticsService.trackFillAccountWithSampleData();
      this.store.dispatch(SampleDataActions.createSampleData());
    } else {
      this.companyAdmins$.pipe(take(1)).subscribe(admins => {
        this.dialogService.openCustom(
          NoAccessDialogComponent,
          { width: DialogWidth.Small },
          {
            title: this.i18nService.translate('app.onboarding.no_access_dialog.add_data.title'),
            description: this.i18nService.translate(
              'app.onboarding.no_access_dialog.add_data.description',
            ),
            imageSrc: 'assets/images/onboarding/adding-sample-data.svg',
            admins: admins.slice(0, 3),
          },
        );
      });
    }
  }
}
